'use client';

import { useRef, useState } from 'react';
import { DockSidebar } from '@/components/dashboard/DockSidebar';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import { clampDockSheetWidth } from './copSidePanelWidth';
import type { LaunchMode } from '@/components/dashboard/DockBayRow';
import type { DockOperationalPhase } from '@/lib/dock-status';
import type { DockCameraSlot } from '@/lib/dock-camera';
import type { DockAckPayload, DockBay, DockCapability, DockPositionCheck, DockStatus, LiveDockStatus } from '@/types/dock';

interface CopDockSheetProps {
  data: LiveDockStatus | null;
  /** Straight through to the sidebar's position badge. */
  positionCheck?: DockPositionCheck;
  dockId: string;
  dockCallsign: string | null;
  registryStatus: DockStatus;
  registryIsOnline: boolean;
  capabilities: DockCapability[];
  bays: DockBay[];
  baysError: string | null;
  operationalPhase: DockOperationalPhase | null;
  airborneDrones: ReadonlyMap<string, { lastSeenAt: string; altitudeM: number }>;
  selectedBayDroneId: string | null;
  onSelectBayDrone: (droneId: string | null) => void;
  onLaunch: (droneId: string, mode: LaunchMode) => void;
  onGoToDrone: (droneId: string) => void;
  subscribeDockAck: (cb: (ack: DockAckPayload) => void) => () => void;
  onClose: () => void;
  /** Straight through to the sidebar's camera keys. The windows they open are
   *  the COP page's, not this sheet's — this sheet is unmounted by a selection
   *  change and a window owned from inside it would go with it. */
  openCameras: readonly DockCameraSlot[];
  onOpenCamera: (cam: DockCameraSlot) => void;
  /** Owned by the COP view: it publishes the width as --dock-sheet-width so the
   *  route builder can anchor beside the sheet. */
  width: number;
  onWidthChange: (width: number) => void;
}

/* The width vocabulary moved to `copSidePanelWidth`, a pure sibling, when the
   right column started floating: the number is no longer a fact about this
   sheet's box but the inset the map's right-edge chrome stands clear of. Still
   exported from here so the four sheets and the page keep their imports. */
export {
  MIN_DOCK_SHEET_WIDTH,
  MAX_SHEET_WIDTH,
  clampDockSheetWidth,
} from './copSidePanelWidth';

/**
 * Resizable shell that docks the dock sidebar to the right of the COP map.
 * Layout only — every operational concern lives in DockSidebar.
 */
export function CopDockSheet({
  data,
  positionCheck,
  dockId,
  dockCallsign,
  registryStatus,
  registryIsOnline,
  capabilities,
  bays,
  baysError,
  operationalPhase,
  airborneDrones,
  selectedBayDroneId,
  onSelectBayDrone,
  onLaunch,
  onGoToDrone,
  subscribeDockAck,
  onClose,
  openCameras,
  onOpenCamera,
  width,
  onWidthChange,
}: CopDockSheetProps) {
  const [collapsed, setCollapsed] = useState(false);

  // The width at the moment of the press — usePointerDrag reports deltas from
  // there, so the drag never reads a width that changed under it.
  const startWidth = useRef(width);
  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'ew-resize',
    onDragStart: () => {
      startWidth.current = width;
    },
    // The panel is anchored right, so dragging left widens it.
    onDrag: ({ dx }) =>
      onWidthChange(clampDockSheetWidth(startWidth.current - dx, window.innerWidth)),
  });

  return (
    <div
      className={`
        flex flex-col min-h-0 overflow-hidden
        lg:w-[var(--dock-sheet-width)] lg:shrink-0 lg:relative lg:z-auto lg:h-auto
        fixed bottom-0 left-0 right-0 z-40 max-w-[100vw]
        rounded-t-xl border-t border-subtle bg-popover/90 backdrop-blur-sm
        shadow-sheet
        lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:backdrop-blur-none
      `}
    >
      <button
        type="button"
        aria-label="Resize dock sidebar"
        onPointerDown={onResizePointerDown}
        className="hidden lg:block absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize border-0 bg-transparent p-0 hover:bg-status-blue/30 focus:outline-none focus-visible:bg-primary"
      />

      <button
        type="button"
        className="lg:hidden flex flex-col items-center justify-center gap-1.5 py-2.5 w-full touch-manipulation border-b divider"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand dock panel' : 'Collapse dock panel'}
      >
        <div className="w-10 h-1 rounded-full bg-muted-foreground/40" aria-hidden />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Dock</span>
      </button>

      <div
        className={
          collapsed
            ? 'hidden lg:flex lg:flex-1 lg:min-h-0 lg:overflow-hidden'
            : 'flex flex-col flex-1 min-h-0 max-h-[min(60vh,36rem)] lg:max-h-full overflow-hidden'
        }
      >
        <DockSidebar
          dockId={dockId}
          callsign={dockCallsign}
          positionCheck={positionCheck}
          live={data}
          registryStatus={registryStatus}
          registryIsOnline={registryIsOnline}
          capabilities={capabilities}
          bays={bays}
          baysError={baysError}
          operationalPhase={operationalPhase}
          airborneDrones={airborneDrones}
          selectedBayDroneId={selectedBayDroneId}
          onSelectBayDrone={onSelectBayDrone}
          onLaunch={onLaunch}
          onGoToDrone={(droneId) => {
            // Below lg this sheet is a fixed bottom overlay covering the map
            // region the drone gets centered into — collapse it so the operator
            // actually sees the aircraft. No-op on desktop: `collapsed` only
            // hides content below lg (`hidden lg:flex`).
            setCollapsed(true);
            onGoToDrone(droneId);
          }}
          subscribeDockAck={subscribeDockAck}
          onClose={onClose}
          openCameras={openCameras}
          onOpenCamera={onOpenCamera}
        />
      </div>
    </div>
  );
}
